import { useState, useEffect } from 'react'
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider'
import { useUserContext } from '../providers/UserContextProvider'
import { api } from '../api/client'
import { FeedbackDialog } from '@edition/controls'
import { SAAS_ENABLED } from '@edition/ui'
import { useUnreadNotificationCount } from '../features/stakeholder-status/useStakeholderNotifications'
import {
  LayoutDashboard,
  Building2,
  Users,
  LogOut,
  Wallet,
  CheckSquare,
  Settings,
  KeyRound,
  History,
  Sparkles,
  ShieldCheck,
  Menu,
  X,
  ArrowLeft,
  Languages,
  Blocks,
  CalendarRange,
  MessageSquarePlus,
  Inbox,
  Bell,
  type LucideIcon,
} from 'lucide-react'

type NavItem = { to: string; icon: LucideIcon; label: string; badge?: number; end?: boolean; section?: string }
const WORKSPACE_RETURN_KEY = 'okr-saas-workspace-return'

function workspaceReturnPath(): string {
  const path = sessionStorage.getItem(WORKSPACE_RETURN_KEY)
  return path?.startsWith('/') && !path.startsWith('//') && !path.startsWith('/admin') ? path : '/'
}

/**
 * Layout for the /admin console. Hosts configuration surfaces only —
 * structure, people, access, billing, identity, integrations — with its own
 * navigation. Work surfaces live in the main Layout; dual-hat users switch
 * back via "Back to workspace".
 */
export function AdminLayout() {
  const { user, logout, hasRole } = useAuth()
  const { userDetail, adminOrganizationId, workMemberships, isPureAdmin } = useUserContext()
  const { t } = useTranslation()
  const location = useLocation()

  const { data: pendingCountData } = useQuery({
    queryKey: ['approvals', 'count', adminOrganizationId],
    queryFn: () => api.approvals.countPending(adminOrganizationId!).then(r => r.data as { count: number }),
    enabled: !!adminOrganizationId,
    refetchInterval: 30_000,
  })
  const pendingCount = pendingCountData?.count ?? 0
  // An admin who also works reads these in the workspace's newsfeed, which has the same badge.
  const unreadUpdates = useUnreadNotificationCount(isPureAdmin && !!userDetail)

  const navItems: NavItem[] = [
    { to: '/admin', icon: LayoutDashboard, label: t('nav.adminOverview'), end: true },
    ...(isPureAdmin
      ? [{ to: '/admin/updates', icon: Bell, label: t('nav.updates'), badge: unreadUpdates > 0 ? unreadUpdates : undefined }]
      : []),
    { to: '/admin/organizations', icon: Building2, label: t('nav.organizations') },
    { to: '/admin/users', icon: Users, label: t('nav.users') },
    { to: '/admin/approvals', icon: CheckSquare, label: t('nav.approvals'), badge: pendingCount > 0 ? pendingCount : undefined },
    ...(SAAS_ENABLED ? [{ to: '/admin/billing', icon: Wallet, label: t('nav.billing') }] : []),
    { to: '/admin/identity', icon: ShieldCheck, label: t('nav.identity') },
    { to: '/admin/api-clients', icon: KeyRound, label: t('nav.apiClients') },
    // Action-history audit query is super_admin-only (no org column to scope
    // against — see AuditLogController.getActionHistory), so hide the link
    // for plain org admins instead of sending them to a 403.
    ...(SAAS_ENABLED && hasRole('super_admin')
      ? [
          { to: '/admin/platform-users', icon: Users, label: t('nav.platformUsers'), section: t('nav.platformAdministration') },
          { to: '/admin/audit-log', icon: History, label: t('nav.auditLog') },
          // The platform's own feedback queue: the same super-admin-only backend guard as the audit log.
          ...(SAAS_ENABLED ? [{ to: '/admin/feedback', icon: Inbox, label: t('nav.feedbackQueue') }] : []),
        ]
      : []),
    { to: '/admin/ai-settings', icon: Sparkles, label: t('nav.aiSettings') },
    { to: '/admin/periods', icon: CalendarRange, label: t('nav.periods') },
    { to: '/admin/operating-model', icon: Blocks, label: t('nav.operatingModel') },
    { to: '/admin/terminology', icon: Languages, label: t('nav.terminology') },
    { to: '/setup', icon: Settings, label: t('nav.setup') },
  ]

  const displayName = userDetail?.fullName || user?.name || user?.email
  const userInitials = displayName
    ? displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?'

  const hasWorkspace = workMemberships.length > 0
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  // Platform super-admins read the feedback; they do not send it.
  const canSendFeedback = SAAS_ENABLED && !hasRole('super_admin')

  useEffect(() => {
    setIsMobileNavOpen(false)
  }, [location.pathname])

  function renderNavItems(onNavigate?: () => void) {
    return navItems.map((item) => (
      <div key={item.to}>
      {item.section && <h2 className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider sidebar-muted">{item.section}</h2>}
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          `sidebar-nav-item flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60${isActive ? ' active' : ''}`
        }
      >
        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{item.label}</span>
        {item.badge !== undefined && (
          <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {item.badge}
          </span>
        )}
      </NavLink>
      </div>
    ))
  }

  const userPanel = (
    <div className="shrink-0 border-t sidebar-divider p-4 space-y-3">
      {hasWorkspace && (
        <Link
          to={workspaceReturnPath()}
          className="sidebar-icon-btn w-full justify-start gap-2 px-3 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('nav.backToWorkspace')}
        </Link>
      )}
      <div className="flex items-center gap-3">
        <div className="sidebar-avatar h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">
          {userInitials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="sidebar-user-name text-sm font-semibold truncate">{displayName}</p>
          <p className="sidebar-muted text-xs truncate">{user?.email}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Link
          to="/preferences"
          className="sidebar-icon-btn justify-start gap-2 px-3 text-sm font-medium"
          title={t('common.settings')}
          aria-label={t('common.settings')}
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
          {t('common.settings')}
        </Link>
        <button
          type="button"
          onClick={logout}
          className="sidebar-icon-btn justify-start gap-2 px-3 text-sm"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          {t('common.signOut')}
        </button>
        {canSendFeedback && (
          <button
            type="button"
            onClick={() => setShowFeedback(true)}
            className="sidebar-icon-btn col-span-2 justify-start gap-2 px-3 text-sm"
            aria-haspopup="dialog"
          >
            <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
            {t('feedback.open')}
          </button>
        )}
      </div>
    </div>
  )

  const sidebarHeader = (
    <div className="flex h-16 flex-col items-center justify-center border-b sidebar-divider">
      <span className="sidebar-logo-text text-lg font-bold tracking-tight font-mono">Gabai</span>
      <span className="sidebar-muted text-[10px] font-semibold uppercase tracking-[0.2em]">{t('nav.administration')}</span>
    </div>
  )

  return (
    <div className="app-shell flex h-screen overflow-hidden bg-muted">
      <aside className="sidebar-themed hidden h-screen w-64 shrink-0 flex-col border-r transition-colors duration-200 lg:flex">
        {sidebarHeader}
        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3" aria-label={t('nav.mainNavigation')}>
          {renderNavItems()}
        </nav>
        {userPanel}
      </aside>

      {isMobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            aria-label={t('nav.closeMenu')}
            onClick={() => setIsMobileNavOpen(false)}
          />
          <aside
            id="mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.mainNavigation')}
            className="sidebar-themed absolute inset-y-0 left-0 flex w-[min(22rem,calc(100vw-3rem))] flex-col border-r shadow-xl"
          >
            <div className="flex h-16 items-center justify-between border-b sidebar-divider px-4">
              <span className="sidebar-logo-text text-lg font-bold tracking-tight font-mono">Gabai</span>
              <button
                type="button"
                className="sidebar-icon-btn"
                aria-label={t('nav.closeMenu')}
                onClick={() => setIsMobileNavOpen(false)}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3" aria-label={t('nav.mainNavigation')}>
              {renderNavItems(() => setIsMobileNavOpen(false))}
            </nav>
            {userPanel}
          </aside>
        </div>
      )}

      <main className="relative h-screen min-w-0 flex-1 overflow-y-auto bg-grid">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-card/95 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            aria-label={t('nav.openMenu')}
            aria-controls="mobile-navigation"
            aria-expanded={isMobileNavOpen}
            onClick={() => setIsMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="min-w-0 px-3 text-center">
            <p className="truncate text-sm font-semibold text-foreground">{t('nav.administration')}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          {canSendFeedback ? (
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              aria-label={t('feedback.open')}
              aria-haspopup="dialog"
              onClick={() => setShowFeedback(true)}
            >
              <MessageSquarePlus className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : (
            <div className="h-10 w-10" aria-hidden="true" />
          )}
        </header>

        <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="relative animate-fade-in p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {canSendFeedback && FeedbackDialog && <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />}
    </div>
  )
}
